import React, { useState, useEffect, useRef } from 'react';
import audioData from '../data/audio.json';

const AudioTranscriptsPage = () => {
  const [selectedAudio, setSelectedAudio] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentTime, setCurrentTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [activeLineIndex, setActiveLineIndex] = useState(0);
  const audioRef = useRef(null);

  useEffect(() => {
    // Set the first audio as selected by default when component mounts
    if (audioData.length > 0 && !selectedAudio) {
      setSelectedAudio(audioData[0]);
    }
  }, []);

  // Filter audio transcripts based on search term
  const filteredAudioTranscripts = audioData.filter(audio => 
    audio.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    audio.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    audio.transcript.some(line => line.text.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Handle audio selection
  const handleAudioSelect = (audio) => {
    setSelectedAudio(audio);
    setCurrentTime(0);
    setIsPlaying(false);
    setActiveLineIndex(0);
  };

  // Open audio in new window
  const handlePlayAudio = () => {
    if (selectedAudio && selectedAudio.audioPath) {
      window.open(selectedAudio.audioPath, '_blank');
    }
  };

  // Play/pause functionality for inline audio
  const togglePlayback = () => {
    if (audioRef.current) {
      if (audioRef.current.paused) {
        audioRef.current.play();
      } else {
        audioRef.current.pause();
      }
    }
  };

  // Sync play/pause state
  useEffect(() => {
    if (!audioRef.current) return;
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    audioRef.current.addEventListener('play', handlePlay);
    audioRef.current.addEventListener('pause', handlePause);
    return () => {
      audioRef.current.removeEventListener('play', handlePlay);
      audioRef.current.removeEventListener('pause', handlePause);
    };
  }, [selectedAudio]);

  // Seek to transcript line time
  const handleSeek = (index) => {
    setActiveLineIndex(index);
    if (selectedAudio && audioRef.current) {
      const timeStr = selectedAudio.transcript[index].time;
      const [minutes, seconds] = timeStr.split(':').map(Number);
      const seekTime = minutes * 60 + seconds;
      audioRef.current.currentTime = seekTime;
      setCurrentTime(seekTime);
    }
  };

  // Update currentTime as audio plays
  useEffect(() => {
    if (!audioRef.current) return;
    const updateTime = () => setCurrentTime(Math.floor(audioRef.current.currentTime));
    audioRef.current.addEventListener('timeupdate', updateTime);
    return () => {
      audioRef.current.removeEventListener('timeupdate', updateTime);
    };
  }, [selectedAudio]);

  return (
    <div className="max-w-5xl mx-auto">
      <div className="bg-white rounded-xl shadow-md p-6 mb-6">
        <h1 className="text-2xl font-bold mb-2 text-blue-800">Audio Recordings & Transcripts</h1>
        <p className="text-gray-600 mb-6">
          Listen to key recorded conversations and review synchronized transcripts. 
          These recordings provide crucial evidence supporting the case.
        </p>
        
        <div className="relative mb-6">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg className="h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
            </svg>
          </div>
          <input
            type="text"
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            placeholder="Search transcripts..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-6">
        <div className="md:w-1/3">
          <div className="bg-white rounded-xl shadow-md overflow-hidden">
            <div className="p-4 bg-blue-50 border-b">
              <h2 className="font-semibold text-lg">Available Recordings</h2>
            </div>
            <div className="divide-y divide-gray-200">
              {filteredAudioTranscripts.map((audio) => (
                <div 
                  key={audio.id}
                  className={`p-4 cursor-pointer hover:bg-gray-50 ${selectedAudio?.id === audio.id ? 'bg-blue-50' : ''}`}
                  onClick={() => handleAudioSelect(audio)}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-medium">{audio.title}</h3>
                      <p className="text-sm text-gray-500 mt-1">{audio.description}</p>
                    </div>
                    <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs">{audio.duration}</span>
                  </div>
                  <div className="mt-2 flex items-center text-xs text-gray-500">
                    <svg className="h-4 w-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                    </svg>
                    {new Date(audio.date).toLocaleDateString()}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        
        <div className="md:w-2/3">
          {selectedAudio && (
            <div className="bg-white rounded-xl shadow-md overflow-hidden">
              <div className="p-4 bg-blue-50 border-b">
                <h2 className="font-semibold text-lg">{selectedAudio.title}</h2>
                <p className="text-sm text-gray-500 mt-1">{selectedAudio.description}</p>
              </div>
              
              {/* Audio Player */}
              <div className="p-4 border-b bg-gray-50">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center">
                    <button 
                      className="bg-blue-600 text-white rounded-full p-2 mr-3 hover:bg-blue-700 focus:outline-none"
                      onClick={togglePlayback}
                    >
                      {isPlaying ? (
                        // Pause icon
                        <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M6 4a1 1 0 00-1 1v10a1 1 0 001 1h1a1 1 0 001-1V5a1 1 0 00-1-1H6zm6 0a1 1 0 00-1 1v10a1 1 0 001 1h1a1 1 0 001-1V5a1 1 0 00-1-1h-1z" clipRule="evenodd" />
                        </svg>
                      ) : (
                        // Play icon
                        <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                        </svg>
                      )}
                    </button>
                    <div className="text-sm font-medium">
                      {`${Math.floor(currentTime / 60)}:${(currentTime % 60).toString().padStart(2, '0')} / ${selectedAudio.duration}`}
                    </div>
                  </div>
                  <div className="flex items-center">
                    <audio
                      ref={audioRef}
                      src={selectedAudio.audioPath}
                      preload="metadata"
                      className="hidden"
                    />
                    <button 
                      className="text-blue-500 hover:text-blue-700 ml-2 text-sm font-medium"
                      onClick={() => window.open(selectedAudio.audioPath, '_blank')}
                    >
                      Open Audio in New Window
                    </button>
                    {selectedAudio.transcriptPath && (
                      <button 
                        className="text-blue-500 hover:text-blue-700 ml-4 text-sm font-medium"
                        onClick={() => window.open(selectedAudio.transcriptPath, '_blank')}
                      >
                        Download Transcript
                      </button>
                    )}
                  </div>
                </div>
                <div className="relative h-2 bg-gray-300 rounded-full">
                  <div 
                    className="absolute h-2 bg-blue-600 rounded-full"
                    style={{ width: `${(currentTime / (parseInt(selectedAudio.duration.split(':')[0]) * 60 + parseInt(selectedAudio.duration.split(':')[1]))) * 100}%` }}
                  ></div>
                </div>
              </div>
              
              {/* Transcript */}
              <div className="p-4">
                <div className="mb-4">
                  <h3 className="font-medium text-gray-700 mb-1">Participants</h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedAudio.participants.map((participant, index) => (
                      <span key={index} className="px-2 py-1 bg-gray-100 rounded-full text-xs">
                        {participant}
                      </span>
                    ))}
                  </div>
                </div>
                
                <div className="border rounded-lg overflow-hidden">
                  <div className="p-3 bg-gray-50 border-b text-sm font-medium">
                    Transcript
                  </div>
                  <div className="divide-y divide-gray-200 max-h-96 overflow-y-auto">
                    {selectedAudio.transcript.map((line, index) => (
                      <div
                        key={index}
                        className={`p-3 hover:bg-gray-50 ${activeLineIndex === index ? 'bg-blue-50' : ''}`}
                        onClick={() => handleSeek(index)}
                      >
                        <div className="flex items-start">
                          <span className="text-xs font-medium text-gray-500 w-10">
                            {line.time}
                          </span>
                          <div className="ml-3 flex-1">
                            <span className="font-medium text-sm text-blue-600">{line.speaker}:</span>
                            <p className="text-sm mt-1">{line.text}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AudioTranscriptsPage;